import { useQuery, useQueryClient } from "@tanstack/react-query";
import { asyncGetItem, asyncGetList } from "@/setup/flexbiz-api-provider/coreFunction";
import newsKeys from "../queryKeys/newsKeys";

const DEFAULT_STALE_TIME = 60000; // 1min

export const fetchNewsArticle = async (slug: string, locale = "vi") => {
  const data = await asyncGetList<any[]>({
    collection_name: "news",
    options: {
      limit: 1,
      condition: {
        slug,
      },
    },
  });
  return data?.[0] || null;
};

export const useFetchNewsArticle = (slug: string, locale = "vi") => {
  return useQuery({
    queryKey: newsKeys.detail(slug, locale),
    queryFn: () => fetchNewsArticle(slug, locale),
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!slug,
  });
};

/* -------------------------------------------------------------------------- */

export const fetchNewsArticles = async (options: any = {}) => {
  const locale = options?.condition?.ngon_ngu || "vi";

  // Thử lấy tin tức theo ngôn ngữ yêu cầu trước
  const result = await asyncGetList<any[]>({
    collection_name: "news",
    options: {
      page: 1,
      limit: process.env.NEWS_LOADING_LIMIT,
      sort: {
        date_created: -1,
      },
      ...options,
      condition: {
        isnews: true,
        ngon_ngu: locale,
        shared: true,
        loai_tin_tuc: { $in: ["Tin tức ZENONE", "Tin tức thị trường", "Sự kiện"] },
        ...(options?.condition || {}),
      },
    },
  });

  // Nếu là ngôn ngữ en hoặc ngôn ngữ khác nhưng không có tin tức tương ứng, fallback tự động lấy dữ liệu vi để client tự dịch
  if ((!result || result.length === 0) && locale !== "vi") {
    const fallbackCondition = {
      ...(options?.condition || {}),
      ngon_ngu: "vi",
    };
    return await asyncGetList<any[]>({
      collection_name: "news",
      options: {
        page: 1,
        limit: process.env.NEWS_LOADING_LIMIT,
        sort: {
          date_created: -1,
        },
        ...options,
        condition: {
          isnews: true,
          shared: true,
          loai_tin_tuc: { $in: ["Tin tức ZENONE", "Tin tức thị trường", "Sự kiện"] },
          ...fallbackCondition,
        },
      },
    });
  }

  return result;
};

export const useFetchNewsArticles = (options: any) => {
  return useQuery({
    queryKey: newsKeys.list(options),
    queryFn: () => fetchNewsArticles(options),
    enabled: !!options,
    staleTime: DEFAULT_STALE_TIME,
  });
};

/* -------------------------------------------------------------------------- */

export const fetchNewsArticlesCount = async (options: any = {}) => {
  const locale = options?.condition?.ngon_ngu || "vi";
  const data = await asyncGetList<{ rows_number: number }>({
    collection_name: "news",
    options: {
      ...options,
      count: true,
      condition: {
        ngon_ngu: locale,
        isnews: true,
        shared: true,
        loai_tin_tuc: { $in: ["Tin tức ZENONE", "Tin tức thị trường", "Sự kiện"] },
        ...(options?.condition || {}),
      },
    },
  });

  const count = data?.rows_number || 0;
  if (count === 0 && locale !== "vi") {
    const fallbackData = await asyncGetList<{ rows_number: number }>({
      collection_name: "news",
      options: {
        ...options,
        count: true,
        condition: {
          isnews: true,
          shared: true,
          loai_tin_tuc: { $in: ["Tin tức ZENONE", "Tin tức thị trường", "Sự kiện"] },
          ...(options?.condition || {}),
          ngon_ngu: "vi",
        },
      },
    });
    return fallbackData?.rows_number || 0;
  }

  return count;
};

export const useFetchNewsArticlesCount = (options: any) => {
  return useQuery({
    queryKey: newsKeys.count(options || {}),
    queryFn: () => fetchNewsArticlesCount(options),
    enabled: !!options,
    staleTime: DEFAULT_STALE_TIME,
  });
};
